import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sectors, categories, getProductsByFilters } from './data/products';
import { motion } from 'framer-motion';
import { 
  ChevronDown, 
  ChevronUp, 
  ArrowRight,
  Users, 
  Clock, 
  CheckCircle,
  LayoutGrid,
  LayoutList,
  ExternalLink,
  Star,
  Sparkles
} from 'lucide-react';

// Styles d'animation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Composant Background Animé
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.15),transparent_50%)]" />
    
    {/* Grille d'arrière-plan */}
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
        }}
      />
    </div>

    {/* Particules flottantes */}
    {[...Array(50)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-[#017D7E]/20"
        style={{
          width: Math.random() * 4 + 'px',
          height: Math.random() * 4 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          animation: `float ${Math.random() * 6 + 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 4}s`
        }}
      />
    ))}
  </div>
);

// Composant ViewToggle amélioré
const ViewToggle = ({ view, setView }) => (
  <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1 backdrop-blur-sm border border-white/10">
    <button
      onClick={() => setView('grid')}
      className={`p-2 rounded transition-all ${
        view === 'grid' 
          ? 'bg-gradient-to-r from-[#017D7E] to-[#00A99D] text-white shadow-lg' 
          : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      <LayoutGrid className="w-5 h-5" />
    </button>
    <button
      onClick={() => setView('table')}
      className={`p-2 rounded transition-all ${
        view === 'table' 
          ? 'bg-gradient-to-r from-[#017D7E] to-[#00A99D] text-white shadow-lg' 
          : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      <LayoutList className="w-5 h-5" />
    </button>
  </div>
);

// Composant FilterButton amélioré
const FilterButton = ({ active, onClick, label, Icon }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300 ${
      active 
        ? 'border-green-500 bg-gradient-to-r from-[#017D7E]/20 to-[#00A99D]/20 text-white shadow-lg' 
        : 'border-white/10 text-white/70 hover:border-green-500/50 hover:text-white'
    }`}
    onClick={onClick}
  >
    {Icon && <Icon className="w-5 h-5" />}
    <span className="text-sm whitespace-nowrap">{label}</span>
  </motion.button>
);

// Composant CategoryButton amélioré
const CategoryButton = ({ active, onClick, label }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
      active
        ? 'bg-gradient-to-r from-[#017D7E] to-[#00A99D] text-white shadow-lg'
        : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
    }`}
    onClick={onClick}
  >
    {label}
  </motion.button>
);

// Composant ProductCard amélioré
// Composant ProductCard modifié
const ProductCard = ({ product, onClick }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -5 }}
    className="group bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 
              hover:border-green-500/50 transition-all duration-300 overflow-hidden 
              cursor-pointer shadow-lg hover:shadow-xl"
    onClick={onClick}
  >
    <div className="p-6">
      {/* En-tête - Conservé tel quel */}
      <div className="flex items-start gap-4 mb-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-[#017D7E]/10 to-[#00A99D]/10 
                      group-hover:from-[#017D7E]/20 group-hover:to-[#00A99D]/20 transition-all duration-300">
          <product.icon className="w-6 h-6 text-[#017D7E] group-hover:text-[#00A99D] transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white truncate 
                       group-hover:text-transparent group-hover:bg-clip-text 
                       group-hover:bg-gradient-to-r group-hover:from-[#017D7E] group-hover:to-[#00A99D]">
            {product.name}
          </h3>
          <p className="text-sm text-[#017D7E]/80 truncate">{product.hero.subtitle}</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-white/70 mb-6 group-hover:text-white/80 transition-colors line-clamp-3">
        {product.overview?.shortDescription}
      </p>

      {/* Points clés */}
      <div className="space-y-2 mb-6">
        {product.overview?.keyPoints?.slice(0, 3).map((point, index) => (
          <div key={index} className="flex items-start gap-2 text-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-[#017D7E] mt-2 flex-shrink-0" />
            <div>
              <span className="text-white/90 font-medium">{point.title}</span>
              <span className="text-white/60"> - {point.description}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Footer */}
    <div className="px-6 py-4 border-t border-white/5 bg-gradient-to-r 
                   from-white/5 to-transparent flex justify-between items-center">
      <div className="px-3 py-1 rounded-full text-xs bg-[#017D7E]/10 text-[#017D7E]">
        {categories.find(c => c.id === product.category)?.name || 'Solution'}
      </div>
      <button className="flex items-center gap-2 text-sm font-medium
                       text-[#017D7E] hover:text-[#00A99D] transition-colors">
        En savoir plus
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </motion.div>
);

// Composant ProductTable amélioré
const ProductTable = ({ products, onProductClick }) => (
  <div className="overflow-x-auto rounded-xl border border-white/10 backdrop-blur-sm">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-white/5">
          <th className="py-4 px-6 text-left text-sm font-medium text-white/70">Solution</th>
          <th className="py-4 px-6 text-left text-sm font-medium text-white/70">Catégorie</th>
          <th className="py-4 px-6 text-left text-sm font-medium text-white/70">Secteur</th>
          <th className="py-4 px-6 text-left text-sm font-medium text-white/70">Clients</th>
          <th className="py-4 px-6 text-left text-sm font-medium text-white/70">Action</th>
        </tr>
      </thead>
      <tbody>
        {products.map((product) => (
          <motion.tr 
            key={product.id}
            variants={itemVariants}
            whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            className="border-t border-white/5 transition-colors cursor-pointer"
            onClick={() => onProductClick(product.id)}
          >
            <td className="py-4 px-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-[#017D7E]/10 to-[#00A99D]/10">
                  <product.icon className="w-5 h-5 text-[#017D7E]" />
                </div>
                <div>
                  <div className="font-medium text-white">{product.name}</div>
                  <div className="text-sm text-white/70">{product.subtitle}</div>
                </div>
              </div>
            </td>
            <td className="py-4 px-6 text-white/70">
              {categories.find(c => c.id === product.category)?.name || '-'}
            </td>
            <td className="py-4 px-6 text-white/70">
              {sectors.find(s => s.id === product.sector)?.name || '-'}
            </td>
            <td className="py-4 px-6">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#017D7E]" />
                <span className="text-white/70">50+</span>
              </div>
            </td>
            <td className="py-4 px-6">
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="text-[#017D7E] hover:text-[#00A99D] transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
              </motion.button>
            </td>
          </motion.tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Composant Portfolio principal amélioré
const Portfolio = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('grid');
  const [activeSector, setActiveSector] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const filteredProducts = getProductsByFilters(activeSector, activeCategory);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen text-white relative">
      <AnimatedBackground />

      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative pt-32 pb-16"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#017D7E]/20 
                          to-[#00A99D]/20 text-[#017D7E] mb-6 border border-green-500/20">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>Solutions Innovantes</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Solutions
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                Pour la Finance de Demain
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
              Découvrez notre gamme complète de solutions innovantes pour transformer votre infrastructure financière.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Filtres et Vue */}
      <div className="container mx-auto px-4 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="backdrop-blur-sm bg-white/5 rounded-2xl border border-white/10 p-6"
        >
          <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center mb-6">
            {/* Filtres */}
            <div className="flex flex-wrap gap-3 flex-1">
              <FilterButton
                active={activeSector === 'all'}
                onClick={() => setActiveSector('all')}
                label="Tous les secteurs"
              />
              {sectors.map(sector => (
                <FilterButton
                  key={sector.id}
                  active={activeSector === sector.id}
                  onClick={() => setActiveSector(sector.id)}
                  label={sector.name}
                  Icon={sector.icon}
                />
              ))}
            </div>

            {/* Toggle Vue */}
            <ViewToggle view={view} setView={setView} />
          </div>

          {/* Catégories */}
          <div className="flex flex-wrap gap-3">
            <CategoryButton
              active={activeCategory === 'all'}
              onClick={() => setActiveCategory('all')}
              label="Toutes les catégories"
            />
            {categories.map(category => (
              <CategoryButton
                key={category.id}
                active={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
                label={category.name}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Contenu */}
      <div className="container mx-auto px-4 pb-16">
        {isLoading ? (
          // Skeleton loader amélioré
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[...Array(6)].map((_, i) => (
              <motion.div 
                key={i} 
                variants={itemVariants}
                className="animate-pulse bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10" />
                  <div className="flex-1">
                    <div className="h-6 bg-white/10 rounded-lg mb-2" />
                    <div className="h-4 bg-white/10 rounded-lg w-2/3" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-white/10 rounded-lg w-full" />
                  <div className="h-4 bg-white/10 rounded-lg w-4/5" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : view === 'grid' ? (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => navigate(`/product/${product.id}`)}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <ProductTable 
              products={filteredProducts}
              onProductClick={(id) => navigate(`/product/${id}`)}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;